import React, { useId, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Pill } from './Pill';
import { addressWarnings, capitaliseWords, findRegion } from './addressFormat';
import { normaliseSuburb, stateForSuburb, suburbWarning, useAustraliaPostSuburbs, useSuburbSearch } from './postcodeCheck';

// Shared pieces of a country-aware Address (#157), used by the Commercial
// Entity form, the Entity page's inline edit and Company Info. See
// addressFormat.js for the layouts themselves.

// A State/Province picked from its country's fixed list. A saved value
// that isn't on the list stays as an extra option, so it's kept until a
// value is picked from the list.
export function RegionSelect({ country, value, onChange, ariaLabel, style, autoFocus, onBlur, options }) {
  const match = findRegion(country, value);
  const current = match ? match.value : (value || '');
  return (
    <select
      className="styled-input"
      value={current}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      autoFocus={autoFocus}
      onBlur={onBlur}
      style={{ width: '100%', ...style }}
    >
      <option value="">(none)</option>
      {value && !match && <option value={value}>{value} (not on list)</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// Amber note under the part it's about — the Soft Warning pattern in
// ui-architecture.md.
export function AddressFieldWarning({ message }) {
  if (!message) return null;
  return (
    <div style={{ fontSize: 12, color: '#7a5c00', background: '#fff8e1', border: '1px solid #f0c34d', borderRadius: 6, padding: '4px 8px', marginTop: 4 }}>
      {message}
    </div>
  );
}

// On a saved Address: an amber pill naming what needs a second look.
export function AddressCheckPill({ address }) {
  const messages = Object.values(addressWarnings(address));
  if (!messages.length) return null;
  return <Pill color="#b45309" background="#fdf1e0" title={messages.join('\n')}>Check Address</Pill>;
}

// Australia Post's check of an Australian Suburb, State and Postcode
// (#158). The labels match backend/services/postcodeCheckService.js.
export const POSTCODE_CHECK_LABELS = { matched: 'Checked', mismatch: "Postcode doesn't match", not_checked: 'Not checked' };

// An Australian Suburb, typed (#158). Australia Post's Suburbs drop down
// from the field: before a Postcode is in, those whose names start with
// what's typed (from 3 letters), each with its State and Postcode; once
// it's in, that Postcode's, starting like what's typed, or all of them
// when none do (a browser's own suggestion list hides them all once the
// typing is wrong, when they're needed most). Picking one sets the Suburb
// and its State, and its Postcode when searched by name. An amber note under the field says when
// what's typed isn't one of them, once the field is left, so it doesn't
// flash while typing. Saving always goes ahead. `onChange({ suburb,
// state? })`; `inputProps` styles the input for its form; `renderWarning`
// draws the note in the form's own look.
export function SuburbInput({ address, fetchCheck, onChange, inputProps, renderWarning = (message) => <AddressFieldWarning message={message} /> }) {
  const suburbs = useAustraliaPostSuburbs(address, fetchCheck);
  const noPostcode = address.country === 'Australia' && !/^\d{4}$/.test(String(address.postcode || '').trim());
  const byName = useSuburbSearch(address.suburb, noPostcode, fetchCheck);
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const listId = useId();
  const warning = focused ? undefined : suburbWarning(address, suburbs);

  const typed = normaliseSuburb(address.suburb);
  const starting = typed ? (suburbs || []).filter((s) => normaliseSuburb(s.suburb).startsWith(typed)) : [];
  const options = suburbs ? (starting.length ? starting : suburbs) : (byName || []);
  const showList = open && options.length > 0;
  const heading = suburbs
    ? `Australia Post’s Suburbs for ${suburbs[0]?.postcode}`
    : `Australia Post’s Suburbs starting with ${String(address.suburb || '').trim()}`;

  const change = (value) => {
    const state = stateForSuburb(value, suburbs);
    onChange(state ? { suburb: value, state } : { suburb: value });
  };
  const pick = (option) => {
    onChange(suburbs ? { suburb: option.suburb, state: option.state } : { suburb: option.suburb, state: option.state, postcode: option.postcode });
    setOpen(false);
    setActive(-1);
  };
  const onKeyDown = (e) => {
    if (!options.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && showList && active >= 0) {
      e.preventDefault();
      e.stopPropagation();
      pick(options[active]);
    } else if (e.key === 'Escape' && showList) {
      e.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        {...inputProps}
        value={address.suburb || ''}
        onChange={(e) => { change(e.target.value); setOpen(true); setActive(-1); }}
        onFocus={() => { setFocused(true); setOpen(true); }}
        onClick={() => setOpen(true)}
        onBlur={() => { setFocused(false); setOpen(false); setActive(-1); onChange({ suburb: capitaliseWords(address.suburb) }); }}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
      />
      {showList && (
        <ul
          id={listId}
          role="listbox"
          style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, maxWidth: inputProps?.style?.maxWidth, margin: '4px 0 0', padding: 4, listStyle: 'none', background: '#fff', border: '1px solid #bfc8d6', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto', fontSize: 14, textAlign: 'left' }}
        >
          <li style={{ padding: '4px 8px', fontSize: 12, color: '#5a6b85' }}>{heading}</li>
          {options.map((o, i) => (
            <li
              key={`${o.suburb}|${o.state}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); pick(o); }}
              onMouseEnter={() => setActive(i)}
              style={{ padding: '6px 8px', borderRadius: 4, cursor: 'pointer', background: i === active ? '#e3eaf3' : 'transparent' }}
            >
              {o.suburb} <span style={{ color: '#5a6b85' }}>{o.state} {o.postcode}</span>
            </li>
          ))}
        </ul>
      )}
      {warning && renderWarning(warning)}
    </div>
  );
}

// On a saved Australian Address: Australia Post's check. A mismatch or an
// Address that couldn't be checked is flagged, with Re-check when given.
export function PostcodeCheckPill({ status, onRecheck, checking }) {
  if (!status) return null;
  const colours = {
    matched: { color: '#15803d', background: '#ecfdf3' },
    mismatch: { color: '#b45309', background: '#fdf1e0' },
    not_checked: { color: '#475569', background: '#eef1f5' },
  }[status];
  if (!colours) return null;
  const title = {
    matched: 'Australia Post lists this Suburb under this State and Postcode',
    mismatch: 'Australia Post doesn’t list this Suburb under this State and Postcode',
    not_checked: 'Not yet checked with Australia Post',
  }[status];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Pill color={colours.color} background={colours.background} title={title}>{POSTCODE_CHECK_LABELS[status]}</Pill>
      {onRecheck && status !== 'matched' && (
        <button
          type="button"
          onClick={onRecheck}
          disabled={checking}
          title="Check this Address with Australia Post"
          style={{ background: 'none', border: 'none', color: '#1976d2', cursor: checking ? 'not-allowed' : 'pointer', textDecoration: 'underline', fontSize: 12, padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          {checking && <Loader2 size={12} style={{ animation: 'postcode-check-spin 0.8s linear infinite' }} />}
          {checking ? 'Checking...' : 'Re-check'}
        </button>
      )}
      <style>{'@keyframes postcode-check-spin { to { transform: rotate(360deg); } }'}</style>
    </span>
  );
}
